// Interactive list for viewing and adding nested subtasks.
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';

import TaskGroupPanel from '@/features/tasks/components/lists/TaskGroupPanel';
import { TaskGroupSubtasksPanel } from '@/features/tasks/components/lists/TaskGroupSubtasksPanel';
import { useT } from '@/i18n/useT';
import { buildSharedTaskListProps } from './TaskGroupSubtasks.helpers';

/**
 * Reusable subtask panel used in both DetailPane and TaskForm.
 *
 * @param {Object} props
 * @param {string} props.parentId
 * @param {import('@/types').Task[]} props.subtasks
 * @param {Function} props.onSaveSubtask
 * @param {boolean} [props.isSortable=false]
 * @param {string|null} [props.droppableId=null]
 * @param {Object|null} [props.droppableData=null]
 * @param {string[]} [props.duplicateOrderKeyTaskIds]
 * @param {Set<string>} [props.plannedParentIds]
 * @param {Set<string>} [props.waitingForParentIds]
 * @param {boolean} [props.closeOnSave=false]
 * @param {string} [props.emptyStateMessage]
 * @param {boolean} [props.showCompleted=true]
 * @param {boolean} [props.disableRowNavigate=false] — skip Enter/dblclick modal navigate (TaskForm modal)
 */
const TaskGroupSubtasks = ({
  parentId,
  subtasks,
  onSaveSubtask,
  isSortable = false,
  droppableId = null,
  droppableData = null,
  duplicateOrderKeyTaskIds,
  plannedParentIds,
  waitingForParentIds,
  closeOnSave = false,
  emptyStateMessage = 'No subtasks yet.',
  showCompleted = true,
  disableRowNavigate = false,
}) => {
  const t = useT();
  const [isAddingSubtask, setIsAddingSubtask] = useState(false);
  const addSessionRef = useRef(0);
  const [selectedSubtaskIds, setSelectedSubtaskIds] = useState([]);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const pendingCompleteIds = useRef(new Set());
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (pendingCompleteIds.current.size === 0) return;
    let changed = false;
    for (const id of pendingCompleteIds.current) {
      const task = subtasks.find((st) => st.id === id);
      if (!task || task.completed) {
        pendingCompleteIds.current.delete(id);
        changed = true;
      }
    }
    if (changed) forceUpdate((n) => n + 1);
  }, [subtasks]);

  const handleBeforeToggleComplete = useCallback((taskId) => {
    pendingCompleteIds.current.add(taskId);
    forceUpdate((n) => n + 1);
  }, []);

  const incompleteSubtasks = useMemo(
    () =>
      subtasks.filter(
        (st) => !st.completed && !pendingCompleteIds.current.has(st.id),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subtasks, pendingCompleteIds.current.size],
  );
  const completedSubtasks = useMemo(
    () => subtasks.filter((st) => st.completed),
    [subtasks],
  );

  const { setNodeRef, isOver } = useDroppable({
    id: droppableId ?? `subtasks-noop-${parentId}`,
    data: droppableData ?? {},
  });

  const handleSave = async (data) => {
    const session = addSessionRef.current;
    await onSaveSubtask(data);
    if (closeOnSave && addSessionRef.current === session)
      setIsAddingSubtask(false);
  };

  const sharedTaskListProps = buildSharedTaskListProps({
    isSortable,
    duplicateOrderKeyTaskIds,
    plannedParentIds,
    waitingForParentIds,
    selectedSubtaskIds,
    setSelectedSubtaskIds,
    editingTaskId,
    setEditingTaskId,
    disableRowNavigate,
  });

  return (
    <TaskGroupPanel
      title={t('taskgroupsubtasks.title', 'Sub-tasks')}
      testId="panel-sub-tasks"
      tasks={subtasks}
      collapsible={false}
    >
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          '--parent-context-color': 'var(--context-color)',
        }}
      >
        <TaskGroupSubtasksPanel
          parentId={parentId}
          subtasks={subtasks}
          droppableId={droppableId}
          isOver={isOver}
          setNodeRef={setNodeRef}
          isAddingSubtask={isAddingSubtask}
          emptyStateMessage={emptyStateMessage}
          showCompleted={showCompleted}
          incompleteSubtasks={incompleteSubtasks}
          completedSubtasks={completedSubtasks}
          sharedTaskListProps={sharedTaskListProps}
          handleBeforeToggleComplete={handleBeforeToggleComplete}
          handleSave={handleSave}
          onStartAdding={() => {
            addSessionRef.current += 1;
            setIsAddingSubtask(true);
          }}
          onCancelAdding={() => setIsAddingSubtask(false)}
          addSubtaskLabel={t('contextMenu.addSubtask', 'Add subtask')}
        />
      </Box>
    </TaskGroupPanel>
  );
};

export default TaskGroupSubtasks;
